import React, {useEffect, HTMLAttributes, useContext} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Feedback from "../../feedback/feedback-container";
import { User, UserWithLicenseUsage } from "../../../model/User";
import { FeedbackEntry } from "../../feedback";
import UserUtils from "../../../utils/user";
import { License } from "../../../model/entitlement/License";
import LicenseUtils from "../../../utils/licensed-item";
import Table, { TableColumn } from "../../table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmModal from "../confirm-modal/confirm-modal-container";
import * as ActionTypes from "../../../actions/actionTypes";
import LicenseValidityBadge, {
  resolveLicenseValidity,
} from "../../badges/license-validity-badge";
import { TABLE_SEARCH_THRESHOLD } from "../../table/table-view";
import { ModalKeys } from "../../pages/users";
import UserStatusBadge from "../../badges/user-status-badge";
import { ClosableModalWrapperComponentProps } from "../modal";
import NavigateAfterAction from "../../navigate-after-action";
import { UserWithStatusValues } from "../../../localization/user";
import {LicenseUsageByUsersLabels } from "../../../localization/license-usage-by-users";
import { LicenseValues } from "../../../localization/license";
import "./license-usage-modal-view.scss";
import { LicenseConsumptionLabels } from "../../../localization/license-consumption";
import {
  UserLicensedItemAssignmentsLabels,
  UserLicensedItemAssignmentsValues,
} from "../../../localization/user-licensed-item-assignments";
import { UserLicenseAssignmentRequestErrors } from "../../../localization/user-license-assignment-request/";
import {ReleaseLicenseLeaseAction} from "../../../actions/actionTypes";
import TooltipTrigger from "../../tooltip-trigger";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import {LicenseAssignmentWithSessions} from "../../../model/entitlement/LicenseAssignmentWithSessions";

//<editor-fold desc="Props">

const resolveUserName = (
  user: User | undefined | null,
  intl: { formatMessage: (v: any, v2?: any) => string }
) =>
  UserUtils.resolveDisplayName(
    user,
    intl.formatMessage(UserWithStatusValues.displayName.undefined)
  );

export interface LicenseUsageModalStateProps {
  onSetActiveSearch: (s: string) => void;
  activeSearch?: string;
  onToggleReservation: (id: string) => void;
  toUpdate: any[];

  onToggleDenial: (id: string) => void;
  availableSeats: number|undefined;
  onAddLeaseToBeReleased: (release: {id:string, assignment: LicenseAssignmentWithSessions}) => void;
  onRemoveLeaseToBeReleased: (release: {id:string, assignment: LicenseAssignmentWithSessions}) => void;
  releaseLeases: {id:string, assignment: LicenseAssignmentWithSessions}[];
}
export interface LicenseUsageModalDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}

export interface LicenseUsageModalAdminProps {
  adminsLoaded: boolean;
  onLoadAdmins: () => void;
}
export interface LicenseUsageModalLicenseIdProps {
  licenseId?: string | undefined;
}
export interface LicenseUsageModalVisibilityProps
  extends Pick<
    ClosableModalWrapperComponentProps,
    "show" | "onClose" | "onExited"
  > {
  onShowFeedback: (feedback: FeedbackEntry | FeedbackEntry[]) => void;
}
export interface LicenseUsageModalDataProps
  extends LicenseUsageModalAdminProps,
    Pick<ClosableModalWrapperComponentProps, "isReady"> {
  license?: License | undefined | null;
  onLoadLicense?: (id: string) => void;
  onApply?: (
    licenseId: string,
    usrs: UserWithLicenseUsage[] | undefined
  ) => Promise<ActionTypes.MultiAction | undefined>;

  users?: UserWithLicenseUsage[] | null;
  onLoadUsers?: (licenseId: string) => void;
  isReady: boolean;
  onReleaseLicenseLease: (release: { id: string, assignment?: LicenseAssignmentWithSessions }[]) => Promise<ReleaseLicenseLeaseAction>;
}
export interface LicenseUsageModalProps
  extends LicenseUsageModalDOMProps,
    LicenseUsageModalLicenseIdProps,
    LicenseUsageModalVisibilityProps,
    LicenseUsageModalStateProps,
    LicenseUsageModalDataProps {}
//</editor-fold>

function LicenseUsageModal(props: LicenseUsageModalProps) {
  //<editor-fold desc="Local variables">
  let {
    show,
    onClose,
    onShowFeedback,
    toUpdate,
    onToggleReservation,
    onToggleDenial,
    onSetActiveSearch,
    activeSearch,
    license,
    licenseId,
    onLoadLicense,
    onLoadUsers,
    onApply,
    users,
    adminsLoaded,
    onLoadAdmins,
    availableSeats,
    isReady,
    onExited,
    onReleaseLicenseLease,
    onAddLeaseToBeReleased,
    onRemoveLeaseToBeReleased,
    releaseLeases,
  } = props;

  const { conf } = useContext(UIConfiguration);
  const toReserve = toUpdate.length
    ? toUpdate.filter((a) => a.addReservation).map((a) => a.id)
    : [];
  const toRelease = toUpdate.length
    ? toUpdate.filter((a) => a.removeReservation).map((a) => a.id)
    : [];
  const toDeny = toUpdate.length
    ? toUpdate.filter((a) => a.addDenial).map((a) => a.id)
    : [];
  const toAllow = toUpdate.length
    ? toUpdate.filter((a) => a.removeDenial).map((a) => a.id)
    : [];
  // this is more like a variable than a hook
  const intl = useIntl();

  if (show && license === undefined && !(licenseId || onLoadLicense)) {
    throw new Error(
      "LicenseUsageModal: Required props missing, either license or licenseId && onLoadLicense must be defined"
    );
  }
  if (show && !onApply) {
    throw new Error(
      "LicenseUsageModal: Required props missing, onApply must be defined"
    );
  }
  //</editor-fold>
  useEffect(() => {
    if (show && !adminsLoaded && onLoadAdmins) {
      onLoadAdmins();
    }
  }, [show, adminsLoaded, onLoadAdmins]);
  const licenseObjId = license ? license.id : undefined;
  useEffect(() => {
    if (
      show &&
      adminsLoaded &&
      (licenseObjId === undefined ||
        (licenseObjId && licenseObjId !== licenseId)) &&
      licenseId &&
      onLoadLicense &&
      onLoadUsers
    ) {
      onLoadLicense(licenseId);
      onLoadUsers(licenseId);
    }
  }, [show, adminsLoaded, licenseObjId, licenseId, onLoadLicense, onLoadUsers]);
  useEffect(() => {
    if (
      show &&
      adminsLoaded &&
      !users &&
      onLoadUsers &&
      (licenseObjId || licenseId)
    ) {
      onLoadUsers(
        licenseId || (licenseObjId ? (licenseObjId as string) : "not_possible")
      );
    }
  }, [show, adminsLoaded, users, onLoadUsers, licenseId, licenseObjId]);
  const columns: TableColumn[] = [
    {
      key: "id",
      label: intl.formatMessage(LicenseUsageByUsersLabels.id),
      isTechnical: true,
      hidden: true,
    },
    {
      key: "name",
      isDummy: true,
      label: intl.formatMessage(LicenseUsageByUsersLabels.name),
      sortable: true,
      className: "w-md-40 w-sm-33",
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const t = props.rendererData.resolveValue(props.row);
        return (
          <>
            <span key={"link_" + props.row.id} className={"mr-2"}>
              <NavigateAfterAction
                href={"/users/" + props.row.id + "/" + ModalKeys.show}
                action={onClose}
              >
                <strong className={"mr-1"}>{t[0]}</strong>
                {t[1] && <small>{" <" + t[1] + ">"}</small>}
              </NavigateAfterAction>
            </span>
            <UserStatusBadge
              status={UserUtils.resolveUserStatus(props.row)}
              withLabel={true}
            />
          </>
        );
      },
      tipRenderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const t = props.rendererData.resolveValue(props.row);
        return (
          <>
            {t[0]}, {t[1]}
          </>
        );
      },
      rendererData: {
        resolveValue: (itm: any) => {
          const status = UserUtils.resolveUserStatus(itm);
          return [
            resolveUserName(itm, intl),
            itm.email,
            status
              ? intl.formatMessage((UserWithStatusValues.status as any)[status])
              : status,
          ];
        },
      },
    },
    {
      key: "hasReservation",
      label: intl.formatMessage(LicenseUsageByUsersLabels.hasReservation),
      tooltip: intl.formatMessage({
        defaultMessage:
          "Reserving a license will guarantee the user will always have access to a license. Reserved licenses are not available to other users.",
        description: "Cell heading tooltip",
      }),
      sortable: true,
      align: "center",
      rendererData: {
        availableSeats: availableSeats,
        deactivated: license && license.active === false,
      },
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return (
          <div
            className={
              !props.cell &&
              (props.rendererData.deactivated ||
              (props.rendererData.availableSeats !== undefined && props.rendererData.availableSeats <= 0))
                ? "tooltip-disabled-wrapper d-block"
                : "d-block"
            }
          >
            <button
              data-test-reserve={!props.cell ? props.row.id : undefined}
              data-test-release={props.cell ? props.row.id : undefined}
              className={
                "btn btn-sm btn-block " +
                (props.cell ? "btn-success" : "btn-outline-secondary")
              }
              disabled={
                !props.cell &&
                (props.rendererData.deactivated ||
                    (props.rendererData.availableSeats !== undefined && props.rendererData.availableSeats <= 0))
              }
              onClick={() => {
                onToggleReservation(props.row.id);
              }}
            >
              {props.cell && <FontAwesomeIcon icon="check" />}
              {props.cell && " "}
              {props.cell
                ? intl.formatMessage({
                    defaultMessage: "Release",
                    description:
                      "Button label for toggling license seat reservation",
                  })
                : intl.formatMessage({
                    defaultMessage: "Reserve",
                    description:
                      "Button label for toggling license seat reservation",
                  })}
            </button>
          </div>
        );
      },
      tipRenderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return props.rendererData.deactivated ? (
          props.cell ? (
            <>
              <FormattedMessage
                defaultMessage="Releasing a license is not available as the license has been deactivated."
                description={"tooltip for disabled release license cell"}
              />
            </>
          ) : (
            <>
              <FormattedMessage
                defaultMessage="Reserving a license is not available as the license has been deactivated."
                description={"tooltip for disabled reserve license cell"}
              />
            </>
          )
        ) : undefined;
      },
    },
    {
      key: "hasDenial",
      tooltip: intl.formatMessage({
        defaultMessage: "Blocked users are not able to consume a license.",
        description: "Cell heading tooltip",
      }),
      label: intl.formatMessage(LicenseUsageByUsersLabels.hasDenial),
      sortable: true,
      align: "center",
      rendererData: {
        deactivated: license && license.active === false,
      },
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return (
          <div
            className={
              props.rendererData.deactivated
                ? "tooltip-disabled-wrapper d-block"
                : "d-block"
            }
          >
            <button
              data-test-deny={!props.cell ? props.row.id : undefined}
              data-test-allow={props.cell ? props.row.id : undefined}
              disabled={props.rendererData.deactivated}
              className={
                "btn btn-sm btn-block " +
                (props.cell ? "btn-danger" : "btn-outline-secondary")
              }
              onClick={() => {
                onToggleDenial(props.row.id);
              }}
            >
              {props.cell && <FontAwesomeIcon icon="ban" />}
              {props.cell && " "}
              {props.cell
                ? intl.formatMessage({
                    defaultMessage: "Release",
                    description:
                      "Button label for toggling license seat denial",
                  })
                : intl.formatMessage({
                    defaultMessage: "Block",
                    description:
                      "Button label for toggling license seat denial",
                  })}
            </button>
          </div>
        );
      },
      tipRenderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return props.rendererData.deactivated ? (
          props.cell ? (
            <>
              <FormattedMessage
                defaultMessage="Releasing a blocked license is not available as the license has been deactivated."
                description={"tooltip for disabled deny license cell"}
              />
            </>
          ) : (
            <>
              <FormattedMessage
                defaultMessage="Blocking a license is not available as the license has been deactivated."
                description={"tooltip for disabled release denial license cell"}
              />
            </>
          )
        ) : undefined;
      },
    },
    {
      key: "isConsuming",
      sortable: true,
      isDummy: true,
      label: intl.formatMessage(UserLicensedItemAssignmentsLabels.isConsuming),
      renderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        const t = props.rendererData.resolveValue(props.row);
        return (
          <>
            {t.length ? (
              t.map((m: any) => {
                let from = m.from;
                if (from === "now()") {
                  from = intl.formatDate(new Date(), {
                    dateStyle: "short",
                    timeStyle: "medium",
                  });
                } else if (from) {
                  from = intl.formatDate(new Date(from), {
                    dateStyle: "short",
                    timeStyle: "medium",
                  });
                }
                let to = m.to;
                if (to === "now()") {
                  to = intl.formatDate(new Date(), {
                    dateStyle: "short",
                    timeStyle: "medium",
                  });
                } else if (to) {
                  to = intl.formatDate(new Date(to), {
                    dateStyle: "short",
                    timeStyle: "medium",
                  });
                }
                const isToBeRemoved = releaseLeases.find((f) => f.id === m.leaseId)
                return (
                    <div className={"usage-details" + (isToBeRemoved ? " to-be-removed" : "")}>
                    {conf.functionality?.enableReleaseLicenseLease && <TooltipTrigger
                        tipKey={"release_" + m.leaseId}
                        placement={"auto-end"}
                        tip={
                          isToBeRemoved ?
                              <FormattedMessage
                                  defaultMessage="Cancel license lease removal"
                                  description={"tooltip for cancel remove license lease button"}
                              />
                              :
                              <FormattedMessage
                                  defaultMessage="Remove license lease"
                                  description={"tooltip for remove license lease button"}
                              />
                        }
                    >
                      <button
                          className={"unstyled release"}
                          type={"button"}
                          onClick={() => {
                            if (isToBeRemoved) {
                              onRemoveLeaseToBeReleased({ id: m.leaseId, assignment: m.assignment });
                            } else {
                              onAddLeaseToBeReleased({ id: m.leaseId, assignment: m.assignment });
                            }
                          }}
                      >
                        {isToBeRemoved && <FontAwesomeIcon icon={"ban"} fixedWidth={true}></FontAwesomeIcon>}
                        {!isToBeRemoved && <FontAwesomeIcon icon={"trash-alt"} fixedWidth={true}></FontAwesomeIcon>}
                      </button>
                    </TooltipTrigger> }
                    <TooltipTrigger
                        tipKey={"details_" + m.leaseId}
                        placement={"auto-start"}
                        tip={
                          (
                              <div className="usage-details">
                          {m.id ? (
                                <div>
                                  <strong className="d-inline-block mr-1 ">
                                    {intl.formatMessage(
                                        LicenseConsumptionLabels.hardwareId
                                    )}
                                  </strong>
                                  <span>{m.id}</span>
                                </div>
                            ) : undefined}
                            {m.name ? (
                              <div>
                                <strong className="d-inline-block mr-1">
                                  {intl.formatMessage(
                                    LicenseConsumptionLabels.hardwareName
                                  )}
                                </strong>
                                <span className={"value"}>{m.name}</span>
                              </div>
                            ) : undefined}
                            <div>
                              <FormattedMessage
                                  defaultMessage="The license lease is valid from {from} until {to}"
                                  description={
                                    "Validity information for the license lease. from = formatted date with time for start of validity, to = formatted date with time for end of validity"
                                  }
                                  values={{
                                    from: from,
                                    to: to,
                                  }}
                              />
                            </div>
                        </div>)}
                    >
                      <div className={'usage-details-content'}>
                    {m.name ? (
                      <div>
                        <strong className="d-inline-block text-success mr-1">
                          {intl.formatMessage(
                            LicenseConsumptionLabels.hardwareName
                          )}
                        </strong>
                        <span>{m.name}</span>
                      </div>
                    ) : undefined}
                    {m.id ? (
                    <div>
                      <strong className="d-inline-block text-success mr-1">
                        {intl.formatMessage(
                          LicenseConsumptionLabels.hardwareId
                        )}
                      </strong>
                      <span>{m.id}</span>
                    </div>
                    ) : undefined}
                    <div>
                      <strong className="d-inline-block text-success mr-1">
                        {intl.formatMessage(LicenseConsumptionLabels.from)}
                      </strong>
                      <span>{from}</span>
                    </div>
                    <div>
                      <strong className="d-inline-block text-success mr-1">
                        {intl.formatMessage(LicenseConsumptionLabels.until)}
                      </strong>
                      <span>{to}</span>
                    </div>
                  </div>
                    </TooltipTrigger>
                  </div>
                );
              })
            ) : (

                <TooltipTrigger
                    tipKey={"not-used"}
                    placement={"auto-start"}
                    tip={<>
                      <FontAwesomeIcon icon="times" className="mr-2" />
                      {intl.formatMessage(
                          UserLicensedItemAssignmentsValues.isConsuming.false
                      )}
                    </>}
                >
                  <div className="mx-auto text-center">
                    <FontAwesomeIcon icon="times" className="text-danger" />
                  </div>
                </TooltipTrigger>
            )}
            {conf.functionality?.enableReleaseLicenseLease && t.length > 1 && t.length !== releaseLeases.length ?
                <TooltipTrigger
                    tipKey={"release-all"}
                    placement={"bottom"}
                    tip={
                      <FormattedMessage
                          defaultMessage="Remove all license leases"
                          description={"tooltip for remove license lease button"}
                      />
                    }
                >
                  <button
                      className={"release-all btn btn-block btn-sm btn-outline-danger"}
                      type={"button"}
                      onClick={() => {
                        onAddLeaseToBeReleased(t.map((m: any) => {
                          return {id: m.leaseId, assignment: m.assignment};
                        }).filter((f: string) => !!f));
                      }}
                  >
                    <FormattedMessage
                        defaultMessage="Remove all"
                        description={"label for remove all license leases button"}
                    />
                  </button>
                </TooltipTrigger> : undefined }
          </>
        );
      },
      tipRenderer: (props: {
        cell: any;
        row: any;
        rowIndex: Number;
        rendererData: any;
      }) => {
        return undefined;
      },
      rendererData: {
        resolveValue: (itm: UserWithLicenseUsage) => {
          // Excessive ? operator use is needed as it seems that the state can change while this is running
          return itm.assignments
              ?.map((a) => {
                return a?.sessions?.map((s) => {
                    const t = s?.anchors
                      ? s?.anchors.map((anchor, ind) => {
                          const leaseId = s?.leases && s?.leases.length ? (s?.leases[ind]?.id) : undefined;
                          return { name: anchor?.externalName, id: anchor?.externalId, leaseId, assignment: a };
                        })
                      : [{}];
                    return t.map((m, i) => {
                      return { from: s?.validFrom, to: s?.validUntil, ...m };
                    });
                  });

              })
              .flat(2)
              .filter((f) => !!f) || [];
        },
      },
    },
  ];
  return (
    <ConfirmModal
      onExited={onExited}
      onReloadData={() => {
        if (licenseId && onLoadLicense) {
          onLoadLicense(licenseId);
        }
        if (licenseId && onLoadUsers) {
          onLoadUsers(licenseId);
        }
        if (onLoadAdmins) {
          onLoadAdmins();
        }
      }}
      isReady={isReady}
      size={"lg"}
      id={"license-usage-modal"}
      skipConfirm={show && !license}
      data-test-license-usage-modal={license ? license.id : true}
      title={
        license
          ? intl.formatMessage(
              {
                defaultMessage: "{name}: usage",
                description:
                  "modal heading. 'name' = name of the licensed item",
              },
              {
                name: license.licensedItem
                  ? LicenseUtils.resolveDisplayName(
                      license.licensedItem,
                      intl.formatMessage(
                        LicenseValues.licensedItem.displayName.undefined
                      )
                    )
                  : intl.formatMessage(
                      LicenseValues.licensedItem.displayName.undefined
                    ),
              }
            )
          : intl.formatMessage({
              defaultMessage: "License not found",
              description: "modal heading when the license is not found",
            })
      }
      confirmTitle={
        license
          ? intl.formatMessage(
              {
                defaultMessage: "{name}: confirm changes",
                description:
                  "modal heading for confirming action, 'name' = name of the licensed item",
              },
              {
                name: license.licensedItem
                  ? LicenseUtils.resolveDisplayName(
                      license.licensedItem,
                      intl.formatMessage(
                        LicenseValues.licensedItem.displayName.undefined
                      )
                    )
                  : intl.formatMessage(
                      LicenseValues.licensedItem.displayName.undefined
                    ),
              }
            )
          : undefined
      }
      acceptButton={{
        label: intl.formatMessage({
          defaultMessage: "Confirm",
          description: "confirm changes button label",
        }),
      }}
      cancelButton={{
        label: intl.formatMessage({
          defaultMessage: "Cancel",
          description: "cancel confirmation button label",
        }),
      }}
      confirmContent={
        <>
          <p data-test-confirm-changes-message>
            <FormattedMessage
              defaultMessage="Are you sure you wish to apply the following changes to the license usage?"
              description="confirm changes message"
            />
          </p>
          {conf.functionality?.enableReleaseLicenseLease && releaseLeases.length > 0 && (
              <>
                <strong>
                  <FormattedMessage
                      defaultMessage="Release license leases"
                      description={"heading for summary of license leases to release"}
                  />
                </strong>
                <ul>
                  {releaseLeases.filter((f, i, r) => r.findIndex((fi) => fi.id === f.id) === i).map((u) => (
                      <li key={"toRelease" + u.id}>
                        {resolveUserName(
                            users ? users.find((val) => !!val.assignments?.find((s) => s.id === u.assignment?.id)) : undefined,
                            intl
                        )}
                      </li>
                  ))}
                </ul>
              </>
          )}
          {toReserve.length > 0 && (
            <>
              <strong>
                <FormattedMessage
                  defaultMessage="Add reservation"
                  description={"heading for summary of reservations to add"}
                />
              </strong>
              <ul>
                {toReserve.map((u) => (
                  <li key={"toReserve" + (u as string)}>
                    {resolveUserName(
                      users ? users.find((val) => val.id === u) : undefined,
                      intl
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {toRelease.length > 0 && (
            <>
              <strong>
                <FormattedMessage
                  defaultMessage="Release reservation"
                  description={"heading for summary of reservations to remove"}
                />
              </strong>
              <ul>
                {toRelease.map((u) => (
                  <li key={"toRelease" + (u as string)}>
                    {resolveUserName(
                      users ? users.find((val) => val.id === u) : undefined,
                      intl
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {toDeny.length > 0 && (
            <>
              <strong>
                <FormattedMessage
                  defaultMessage="Block usage"
                  description={"heading for summary of denials to add"}
                />
              </strong>
              <ul>
                {toDeny.map((u) => (
                  <li key={"toDeny" + (u as string)}>
                    {resolveUserName(
                      users ? users.find((val) => val.id === u) : undefined,
                      intl
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {toAllow.length > 0 && (
            <>
              <strong>
                <FormattedMessage
                  defaultMessage="Allow usage"
                  description={"heading for summary of denials to remove"}
                />
              </strong>
              <ul>
                {toAllow.map((u) => (
                  <li key={"toAllow" + (u as string)}>
                    {resolveUserName(
                      users ? users.find((val) => val.id === u) : undefined,
                      intl
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      }
      show={show}
      onClose={onClose}
      backdrop={toUpdate.length > 0 ? "static" : true}
      primaryButton={{
        label: license
          ? intl.formatMessage({
              defaultMessage: "Apply",
              description: "primary button label",
            })
          : intl.formatMessage({
              defaultMessage: "Close",
              description: "clsoe button label",
            }),
        disabled: show && license && toUpdate.length === 0 && releaseLeases.length === 0? true : false,
      }}
      onPrimaryAction={() => {
        if (show && license && users && onApply) {
          const releaseHandler = (releaseResult: any|undefined) => {
            const reservationHandler = (r: any) => {
              const feedbacks: FeedbackEntry[] = [];
              if (r !== 'skipped' && typeof r !== "string") {
                if (!!r && !!r.results && !!r.results.length && !!r.results[0]) {
                  const res = r
                      .results[0] as ActionTypes.ManageUsersLicenseAssignmentsAction;
                  let success;
                  let failure;
                  if (res.userAssignments) {
                    success = Object.keys(res.userAssignments)
                        .map((id) =>
                            users ? users.find((val) => val.id === id) : undefined
                        )
                        .filter((val) => !!val);
                  }
                  if (res.errors) {
                    failure = Object.keys(res.errors)
                        .map((id) =>
                            users ? users.find((val) => val.id === id) : undefined
                        )
                        .filter((val) => !!val);
                  }
                  if (success && success.length) {
                    if (failure && failure.length) {
                      // some succeeded, some failed
                      feedbacks.push({
                        id: "manageAssignments_success_" + res.licenseId,
                        msg: intl.formatMessage(
                            {
                              defaultMessage:
                                  "Updated {name} assignments for {userNames}.",
                              description:
                                  "Success notification for updated license assignments, when some assignments also failed. 'name' = name of the license, 'userNames' = a list of targeted users",
                            },
                            {
                              name:
                                  "<strong>" +
                                  (license && license.licensedItem
                                      ? LicenseUtils.resolveDisplayName(
                                          license.licensedItem,
                                          intl.formatMessage(
                                              LicenseValues.licensedItem.displayName
                                                  .undefined
                                          )
                                      )
                                      : intl.formatMessage(
                                          LicenseValues.licensedItem.displayName.undefined
                                      )) +
                                  "</strong>",
                              userNames:
                                  "<strong>" +
                                  success
                                      .map((usr) => resolveUserName(usr, intl))
                                      .join(", ") +
                                  "</strong>",
                            }
                        ),
                        autoclose: true,
                        type: "success",
                      });
                    } else {
                      // All succeeded
                      feedbacks.push({
                        id: "manageAssignments_success_" + res.licenseId,
                        msg: intl.formatMessage(
                            {
                              defaultMessage: "Updated {name} assignments",
                              description:
                                  "Success notification for updated license assignments. 'name' = name of the license",
                            },
                            {
                              name:
                                  "<strong>" +
                                  (license && license.licensedItem
                                      ? LicenseUtils.resolveDisplayName(
                                          license.licensedItem,
                                          intl.formatMessage(
                                              LicenseValues.licensedItem.displayName
                                                  .undefined
                                          )
                                      )
                                      : intl.formatMessage(
                                          LicenseValues.licensedItem.displayName.undefined
                                      )) +
                                  "</strong>",
                            }
                        ),
                        autoclose: true,
                        type: "success",
                      });
                    }
                  }
                  if (res.errors && failure && failure.length) {
                    const errorsByMessage: any = {};
                    const errKeys = Object.keys(res.errors);
                    for (let i = 0; i < errKeys.length; i += 1) {
                      errorsByMessage[res.errors[errKeys[i]].error] =
                          errorsByMessage[res.errors[errKeys[i]].error]
                              ? errorsByMessage[res.errors[errKeys[i]].error].push(
                                  errKeys[i]
                              )
                              : [errKeys[i]];
                    }
                    const errorMessages = Object.keys(errorsByMessage);
                    if (errorMessages.length === 1) {
                      // Single failure cause
                      if (success && success.length) {
                        // with some succeeding
                        feedbacks.push({
                          id: "manageAssignments_partial_error_" + res.licenseId,
                          msg: intl.formatMessage(
                              {
                                defaultMessage:
                                    "Updating {name} assignments for {userNames} failed due to: {error}",
                                description:
                                    "Failure notification for updated license assignments, when there are multiple causes or successful assignments. 'name' = name of the license, 'userNames' = list of targeted users, 'error' = error message.",
                              },
                              {
                                name:
                                    "<strong>" +
                                    (license && license.licensedItem
                                        ? LicenseUtils.resolveDisplayName(
                                            license.licensedItem,
                                            intl.formatMessage(
                                                LicenseValues.licensedItem.displayName
                                                    .undefined
                                            )
                                        )
                                        : intl.formatMessage(
                                            LicenseValues.licensedItem.displayName
                                                .undefined
                                        )) +
                                    "</strong>",
                                error: !!(UserLicenseAssignmentRequestErrors as any)[
                                    errorMessages[0]
                                    ]
                                    ? "<strong>" +
                                    intl.formatMessage(
                                        (UserLicenseAssignmentRequestErrors as any)[
                                            errorMessages[0]
                                            ]
                                    ) +
                                    "</strong>"
                                    : "<strong>" +
                                    intl.formatMessage({
                                      defaultMessage: "Unknown error.",
                                      description:
                                          "License assignment failure error fallback message",
                                    }) +
                                    "</strong>",
                                userNames:
                                    "<strong>" +
                                    failure
                                        .filter(
                                            (usr) =>
                                                errorsByMessage[errorMessages[0]].findIndex(
                                                    (id: string) =>
                                                        usr && (usr.id as string) === id
                                                ) >= 0
                                        )
                                        .map((usr) => resolveUserName(usr, intl))
                                        .join(", ") +
                                    "</strong>",
                              }
                          ),
                          type: "danger",
                        });
                      } else {
                        // without succeeding
                        feedbacks.push({
                          id: "manageAssignments_error_" + res.licenseId,
                          msg: intl.formatMessage(
                              {
                                defaultMessage:
                                    "Updating {name} assignments failed due to: {error}",
                                description:
                                    "Failure notification for updated license assignments. 'name' = name of the license, 'error' = error message",
                              },
                              {
                                name:
                                    "<strong>" +
                                    (license && license.licensedItem
                                        ? LicenseUtils.resolveDisplayName(
                                            license.licensedItem,
                                            intl.formatMessage(
                                                LicenseValues.licensedItem.displayName
                                                    .undefined
                                            )
                                        )
                                        : intl.formatMessage(
                                            LicenseValues.licensedItem.displayName
                                                .undefined
                                        )) +
                                    "</strong>",
                                error: !!(UserLicenseAssignmentRequestErrors as any)[
                                    errorMessages[0]
                                    ]
                                    ? "<strong>" +
                                    intl.formatMessage(
                                        (UserLicenseAssignmentRequestErrors as any)[
                                            errorMessages[0]
                                            ]
                                    ) +
                                    "</strong>"
                                    : "<strong>" +
                                    intl.formatMessage({
                                      defaultMessage: "Unknown error.",
                                      description:
                                          "License assignment failure error fallback message",
                                    }) +
                                    "</strong>",
                              }
                          ),
                          type: "danger",
                        });
                      }
                    } else if (errorMessages.length > 1) {
                      // Multiple causes
                      for (let i = 0; i < errorMessages.length; i += 1) {
                        feedbacks.push({
                          id:
                              "manageAssignments_partial_error_" +
                              res.licenseId +
                              errorMessages[i],
                          msg: intl.formatMessage(
                              {
                                defaultMessage:
                                    "Updating {name} assignments for {userNames} failed due to: {error}",
                                description:
                                    "Failure notification for updated license assignments, when there are multiple causes or successful assignments. 'name' = name of the license, 'userNames' = list of targeted users, 'error' = error message.",
                              },
                              {
                                name:
                                    (license && license.licensedItem
                                        ? LicenseUtils.resolveDisplayName(
                                            license.licensedItem,
                                            intl.formatMessage(
                                                LicenseValues.licensedItem.displayName
                                                    .undefined
                                            )
                                        )
                                        : intl.formatMessage(
                                            LicenseValues.licensedItem.displayName
                                                .undefined
                                        )) + "</strong>",
                                error: !!(UserLicenseAssignmentRequestErrors as any)[
                                    errorMessages[0]
                                    ]
                                    ? "<strong>" +
                                    intl.formatMessage(
                                        (UserLicenseAssignmentRequestErrors as any)[
                                            errorMessages[0]
                                            ]
                                    ) +
                                    "</strong>"
                                    : "<strong>" +
                                    intl.formatMessage({
                                      defaultMessage: "Unknown error.",
                                      description:
                                          "License assignment failure error fallback message",
                                    }) +
                                    "</strong>",
                                userNames:
                                    "<strong>" +
                                    failure
                                        .filter(
                                            (usr) =>
                                                errorsByMessage[errorMessages[i]].findIndex(
                                                    (id: string) =>
                                                        usr && (usr.id as string) === id
                                                ) >= 0
                                        )
                                        .map((usr) => resolveUserName(usr, intl))
                                        .join(", ") +
                                    "</strong>",
                              }
                          ),
                          type: "danger",
                        });
                      }
                    }
                  }
                }
              }
              if (releaseResult && releaseResult.result) {
                const resultArray = releaseResult.result.length !== undefined ? releaseResult.result : [releaseResult.result];
                const invalidReleaseResults: any[] = [];
                const validReleaseResults = releaseLeases.map((rl) => {
                  if (resultArray.find((rrr: any) => rrr[rl.id])) {
                    return rl;
                  } else {
                    invalidReleaseResults.push(rl);
                    return undefined;
                  }
                }).filter((f) => !!f);
                if (validReleaseResults && validReleaseResults.length) {
                  // success
                  feedbacks.push({
                    id: "releaseLease_success_" + license?.id,
                    msg: intl.formatMessage(
                        {
                          defaultMessage:
                              "Released {name} {leaseCount, plural, =1 {lease} other {leases} } for {userNames}.",
                          description:
                              "Success notification for removing license lease. 'leaseCount' = Amount of leases for choosing plural/singular term for 'lease' in translation, 'name' = name of the license, 'userNames' = a list of targeted users",
                        },
                        {
                          leaseCount: validReleaseResults.length,
                          name: "<strong>" +  license?.licensedItem
                              ? LicenseUtils.resolveDisplayName(
                                  license?.licensedItem || {}, // the sanity check 2 lines above this is somehow does not suffice, and we had to add this stupid and useless object fallback value to satisfy some whining component
                                  intl.formatMessage(
                                      LicenseValues.licensedItem.displayName.undefined
                                  )
                              )
                              : intl.formatMessage(
                                  LicenseValues.licensedItem.displayName.undefined
                              ) + "</strong>",
                          userNames: validReleaseResults.map(
                              (r: any) =>
                                  "<strong>" +
                                  resolveUserName(users?.find((usr) => {
                                    return usr.assignments?.find((ass) => ass.id === r.assignment.id);
                                  }), intl) +
                                  "</strong>"
                          ).filter((f, i, arr) => arr.indexOf(f) === i).join(", "),
                        }
                    ),
                    autoclose: true,
                    type: "success",
                  });
                }
                if (invalidReleaseResults.length) {
                  // failure
                  feedbacks.push({
                    id: "releaseLease_failed_" + license?.id,
                    msg: intl.formatMessage(
                        {
                          defaultMessage:
                              "Releasing {name}'s {leaseCount, plural, =1 {lease} other {leases} } failed for: {userNames}.",
                          description:
                              "Failure notification for removing license lease. 'leaseCount' = Amount of leases for choosing plural/singular term for 'lease' in translation, 'name' = name of the license, 'licenses' = a list of targeted users",
                        },
                        {
                          leaseCount: invalidReleaseResults.length,
                          name: "<strong>" +  license?.licensedItem
                              ? LicenseUtils.resolveDisplayName(
                                  license?.licensedItem || {}, // the sanity check 2 lines above this is somehow does not suffice, and we had to add this stupid and useless object fallback value to satisfy some whining component
                                  intl.formatMessage(
                                      LicenseValues.licensedItem.displayName.undefined
                                  )
                              )
                              : intl.formatMessage(
                              LicenseValues.licensedItem.displayName.undefined
                          ) + "</strong>",
                          userNames: invalidReleaseResults.map(
                              (r: any) =>
                                  "<strong>" +
                                  resolveUserName(users?.find((usr) => {
                                    return usr.assignments?.find((ass) => ass.id === r.assignment.id);
                                  }), intl) +
                                  "</strong>"
                          ).filter((f, i, arr) => arr.indexOf(f) === i).join(", "),
                        }
                    ),
                    type: "danger",
                  });
                }
              }
              if (feedbacks.length) {
                onShowFeedback(feedbacks);
              }
            };
            if (onApply && license && toUpdate.length > 0) {
              onApply(
                  license.id as string,
                  toUpdate.map((a) => {
                    delete a.addDenial;
                    delete a.removeDenial;
                    delete a.addReservation;
                    delete a.removeReservation;
                    return a;
                  })
              ).then(reservationHandler);
            } else {
              reservationHandler('skipped')
            }
          };
          if (releaseLeases.length && conf.functionality?.enableReleaseLicenseLease) {
            onReleaseLicenseLease(releaseLeases).then(releaseHandler);
          } else {
            releaseHandler(undefined);
          }
        }
        onClose();
      }}
      secondaryButton={
        show && license
          ? {
              label: intl.formatMessage({
                defaultMessage: "Cancel",
                description: "secondary button label",
              }),
            }
          : undefined
      }
      onSecondaryAction={show && license ? onClose : () => {}}
    >
      <>
        {show && license && (
          <>
            <Table<UserWithLicenseUsage>
              disableLoadingIndicator={!isReady}
              maxRows={15}
              compact={true}
              reset={false}
              pagination={false}
              data={users ? users : !isReady ? undefined : []}
              identifyingColumn={"id"}
              columns={columns}
              search={(users || []).length > TABLE_SEARCH_THRESHOLD}
              onSearch={onSetActiveSearch}
              activeSearch={activeSearch}
              header={
                <div className={"mr-auto mb-0 pt-1"}>
                  <LicenseValidityBadge
                    validFrom={license.validFrom}
                    validUntil={license.validUntil}
                    validity={resolveLicenseValidity(license)}
                    withLabel={true}
                  />
                  <span
                    className={
                      availableSeats === 0 ? "text-danger ml-2" : "ml-2"
                    }
                  >
                    <FormattedMessage
                      defaultMessage="Available seats remaining: {availableSeats} "
                      description={
                        "Table heading. 'availableSeats' = the amount of available seats"
                      }
                      values={{
                        availableSeats: availableSeats !== undefined ? availableSeats : intl.formatMessage(LicenseValues.seatsAvailable.undefined),
                      }}
                    />
                  </span>
                </div>
              }
            />
          </>
        )}
        {!!show && !!isReady && !license && (
          <Feedback type={"danger"} show={true} asChild={true}>
            <p>
              <FormattedMessage
                defaultMessage="Something went wrong and the license could not be loaded. The license may have been removed or you don't have sufficient access rights."
                description="title to be shown when there is no license to display"
              />
            </p>
          </Feedback>
        )}
      </>
    </ConfirmModal>
  );
}

export default LicenseUsageModal;
